import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MessageSquare, Upload, Download, Facebook, Instagram, Youtube, Move } from 'lucide-react';
import { useAudioEngine } from '../hooks/useAudioEngine';

const PLATFORMS = [
    { id: 'tiktok', name: 'TikTok', bg: '#010101', text: '#FFFFFF' },
    { id: 'instagram', name: 'Instagram', bg: '#E1306C', text: '#FFFFFF' },
    { id: 'facebook', name: 'Facebook', bg: '#1877F2', text: '#FFFFFF' },
    { id: 'youtube', name: 'YouTube', bg: '#FF0000', text: '#FFFFFF' },
];

const PLATFORM_ICONS = {
    tiktok: MessageSquare, instagram: Instagram, facebook: Facebook, youtube: Youtube,
};

const loadImage = (src) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = src;
        img.onload = () => resolve(img);
        img.onerror = (err) => reject(err);
    });
};

function getWrappedLines(ctx, text, maxWidth, maxLines) {
    const words = (text || '').split(' ').filter(Boolean);
    const lines = [];
    let current = '';

    for (const word of words) {
        const test = current ? `${current} ${word}` : word;
        if (ctx.measureText(test).width > maxWidth && current) {
            lines.push(current);
            current = word;
            if (lines.length >= maxLines) break;
        } else {
            current = test;
        }
    }
    if (current && lines.length < maxLines) lines.push(current);

    if (lines.length === maxLines) {
        let last = lines[maxLines - 1];
        while (ctx.measureText(last + '…').width > maxWidth && last.length > 0) last = last.slice(0, -1);
        lines[maxLines - 1] = last + '…';
    }

    return lines.length ? lines : [''];
}

// logoImg is now an explicit parameter — no more implicit global reference
function drawCommentBubble(ctx, platform, username, comment, x, y, s = 1, logoImg = null) {
    const MAX_LINES = 4;
    const bubbleW = 350 * s;
    const r = 12 * s;
    const padX = 12 * s;
    const padV = 12 * s;
    const avatarR = 14 * s;
    const avatarD = avatarR * 2;
    const colGap = 8 * s;
    const textX = x + padX + avatarD + colGap;
    const textW = bubbleW - padX * 2 - avatarD - colGap - 4 * s;

    const usernameFont = `bold ${12 * s}px -apple-system, BlinkMacSystemFont, sans-serif`;
    const subtextFont  = `${9 * s}px -apple-system, BlinkMacSystemFont, sans-serif`;
    const commentFont  = `${11 * s}px -apple-system, BlinkMacSystemFont, sans-serif`;
    const lineH = 14 * s;

    ctx.font = commentFont;
    const lines = getWrappedLines(ctx, comment || 'Your comment here…', textW, MAX_LINES);

    const usernameH  = 13 * s;
    const subtextH   = 11 * s;
    const headerH    = usernameH + 2 * s + subtextH;
    const commentH   = lines.length * lineH;
    const innerH     = headerH + 5 * s + commentH;
    const finalInner = Math.max(innerH, avatarD);
    const bubbleH    = finalInner + padV * 2;

    // Bubble background
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.25)';
    ctx.shadowBlur  = 10 * s;
    ctx.fillStyle   = 'rgba(255,255,255,0.97)';
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + bubbleW, y,           x + bubbleW, y + bubbleH, r);
    ctx.arcTo(x + bubbleW, y + bubbleH, x,           y + bubbleH, r);
    ctx.arcTo(x,           y + bubbleH, x,           y,           r);
    ctx.arcTo(x,           y,           x + bubbleW, y,           r);
    ctx.closePath();
    ctx.fill();
    ctx.restore();

    // Avatar — clipped logo image, or colored circle fallback
    const cx = x + padX + avatarR;
    const cy = y + padV + avatarR;

    if (logoImg) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(cx, cy, avatarR, 0, Math.PI * 2);
        ctx.clip();
        ctx.drawImage(logoImg, cx - avatarR, cy - avatarR, avatarD, avatarD);
        ctx.restore();
    } else {
        ctx.fillStyle = platform.bg;
        ctx.beginPath();
        ctx.arc(cx, cy, avatarR, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = platform.text;
        ctx.font = `bold ${10 * s}px -apple-system, sans-serif`;
        ctx.textAlign = 'center';
        ctx.fillText(platform.name[0], cx, cy + 4 * s);
        ctx.textAlign = 'left';
    }

    // Username
    const baseY = y + padV + usernameH;
    ctx.fillStyle = '#0f172a';
    ctx.font = usernameFont;
    ctx.fillText(username || '@username', textX, baseY);

    // Subtext
    ctx.fillStyle = '#64748b';
    ctx.font = subtextFont;
    ctx.fillText(`comment from ${platform.name}`, textX, baseY + 2 * s + subtextH);

    // Comment lines
    ctx.fillStyle = '#1e293b';
    ctx.font = commentFont;
    const firstY = baseY + 2 * s + subtextH + 5 * s + lineH;
    lines.forEach((line, i) => ctx.fillText(line, textX, firstY + i * lineH));
}

// ─── Component ────────────────────────────────────────────────────────────────
const EmbedComment = ({ setIsExporting, setExportProgress }) => {
    const [videoFile, setVideoFile]   = useState(null);
    const [videoURL,  setVideoURL]    = useState(null);
    const [username,  setUsername]    = useState('');
    const [commentText, setComment]   = useState('');
    const [platform, setPlatform]     = useState(PLATFORMS[0]);
    const [commentPos, setPos]        = useState({ x: 20, y: 60 });
    const [logoObject, setLogoObject] = useState(null);

    const videoRef   = useRef(null);
    const canvasRef  = useRef(null);
    const previewRef = useRef(null);
    const rafRef     = useRef(null);
    const dragRef    = useRef(null);

    const { embedCommentOnVideo, validateFileDuration } = useAudioEngine(null, null);

    // Load platform logo whenever platform changes
    useEffect(() => {
        const path = `/assets/logos/${platform.id}.png`;
        loadImage(path)
            .then(img => setLogoObject(img))
            .catch(()  => setLogoObject(null)); // fall back to colored circle
    }, [platform]);

    // Render loop — logoObject passed explicitly into drawCommentBubble
    const renderPreview = useCallback(() => {
        const canvas = previewRef.current;
        const video  = videoRef.current;
        if (!canvas || !video) return;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        try { ctx.drawImage(video, 0, 0, canvas.width, canvas.height); } catch (_) {}
        drawCommentBubble(ctx, platform, username, commentText, commentPos.x, commentPos.y, 1, logoObject);
        rafRef.current = requestAnimationFrame(renderPreview);
    }, [platform, username, commentText, commentPos, logoObject]);

    useEffect(() => {
        if (videoURL) rafRef.current = requestAnimationFrame(renderPreview);
        return () => cancelAnimationFrame(rafRef.current);
    }, [videoURL, renderPreview]);

    // Drag helpers
    const getXY = (e, canvas) => {
        const rect = canvas.getBoundingClientRect();
        const sx = canvas.width  / rect.width;
        const sy = canvas.height / rect.height;
        const cx = e.touches ? e.touches[0].clientX : e.clientX;
        const cy = e.touches ? e.touches[0].clientY : e.clientY;
        return { x: (cx - rect.left) * sx, y: (cy - rect.top) * sy };
    };

    const onDragStart = (e) => {
        e.preventDefault();
        const { x, y } = getXY(e, previewRef.current);
        dragRef.current = { sx: x, sy: y, ox: commentPos.x, oy: commentPos.y };
    };
    const onDragMove = (e) => {
        if (!dragRef.current) return;
        e.preventDefault();
        const { x, y } = getXY(e, previewRef.current);
        setPos({ x: dragRef.current.ox + x - dragRef.current.sx, y: dragRef.current.oy + y - dragRef.current.sy });
    };
    const onDragEnd = () => { dragRef.current = null; };


    const handleVideoUpload = async (e) => {
        const f = e.target.files[0];
        if (!f) return;

        // Nou rele validateFileDuration ak limit 2 minit
        const check = await validateFileDuration(f, 2);
        
        if (!check.isValid) {
            alert(check.msg); // L ap di: "File exceeds the 2 minute limit."
            e.target.value = ""; // Nou vide input la
            return;
        }

        setVideoFile(f);
        setVideoURL(URL.createObjectURL(f));
    };

    const handleExport = async () => {
        if (!videoFile || !username || !commentText) return alert('Please fill in username and comment first.');
        const preview = previewRef.current;
        const native  = canvasRef.current;
        const sx = native.width  / preview.width;
        const sy = native.height / preview.height;
        const nx = Math.round(commentPos.x * sx);
        const ny = Math.round(commentPos.y * sy);

        const ctx = native.getContext('2d');
        ctx.clearRect(0, 0, native.width, native.height);
        // Pass logoObject so exported image matches the preview exactly
        drawCommentBubble(ctx, platform, username, commentText, nx, ny, sx, logoObject);

        const blob = await new Promise(res => native.toBlob(res, 'image/png'));
        await embedCommentOnVideo(videoFile, blob, setIsExporting, setExportProgress, nx, ny);
    };

    return (
        <div className="max-w-5xl mx-auto space-y-10 animate-in fade-in">
            <div className="text-center space-y-3">
                <h2 className="text-4xl font-black uppercase tracking-tighter">Embed Comment</h2>
                <p className="text-slate-400 text-sm">Replicate the viral "Reply to Comment" look. Drag to reposition.</p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 items-start">

                {/* Form */}
                <div className="bg-slate-900 p-8 rounded-[40px] border border-slate-800 space-y-6">
                    {/* Autofill override — prevents browser from overriding bg to white */}
                    <style>{`
                        .dark-input:-webkit-autofill,
                        .dark-input:-webkit-autofill:hover,
                        .dark-input:-webkit-autofill:focus {
                            -webkit-box-shadow: 0 0 0px 1000px #020617 inset !important;
                            -webkit-text-fill-color: #ffffff !important;
                            caret-color: #ffffff;
                        }
                    `}</style>

                    <div className="space-y-3">
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Platform</label>
                        <div className="grid grid-cols-2 gap-3 w-full mt-2">
                            {PLATFORMS.map(p => {
                                const Icon = PLATFORM_ICONS[p.id];
                                const isActive = platform.id === p.id;
                                return (
                                    <button
                                        key={p.id}
                                        onClick={() => setPlatform(p)}
                                        className={`flex items-center gap-3 px-4 py-3.5 rounded-2xl transition-all w-full ${
                                            isActive
                                                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/20 scale-[1.02]'
                                                : 'bg-slate-950 text-slate-400 hover:bg-slate-800 hover:text-black'
                                        }`}
                                    >
                                        <div className={`p-2 rounded-xl ${isActive ? 'bg-white/20' : 'bg-slate-800'}`}>
                                            <Icon size={16} strokeWidth={1.75} />
                                        </div>
                                        <span className="text-xs font-bold tracking-wide">{p.name}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Name or Username</label>
                        <input
                            className="dark-input w-full bg-slate-950 border border-slate-800 focus:border-indigo-500 outline-none p-4 rounded-2xl text-black font-bold placeholder:text-slate-600 transition-colors"
                            placeholder="@username"
                            value={username}
                            onChange={e => setUsername(e.target.value)}
                            autoComplete="off"
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center justify-between">
                            Comment
                            <span className="text-slate-700 normal-case font-normal tracking-normal">max 4 lines</span>
                        </label>
                        <textarea
                            className="dark-input w-full bg-slate-950 border border-slate-800 focus:border-indigo-500 outline-none p-4 rounded-2xl text-black h-28 resize-none placeholder:text-slate-600 transition-colors"
                            placeholder="Type the comment here..."
                            value={commentText}
                            onChange={e => setComment(e.target.value)}
                        />
                    </div>

                    {videoURL && (
                        <>
                            <div className="flex items-center gap-3 bg-indigo-600/10 border border-indigo-600/20 rounded-2xl p-4">
                                <Move size={16} className="text-indigo-400 shrink-0" />
                                <p className="text-indigo-300 text-xs font-bold">Drag the comment bubble on the preview to reposition it</p>
                            </div>
                            <button
                                onClick={handleExport}
                                className="w-full bg-indigo-600 hover:bg-indigo-500 active:scale-95 text-white py-5 rounded-3xl font-black uppercase tracking-widest text-xs transition-all shadow-lg shadow-indigo-500/20 flex items-center justify-center gap-3"
                            >
                                <Download size={18} /> Export Video with Comment
                            </button>
                        </>
                    )}
                </div>

                {/* Preview */}
                <div className="space-y-4">
                    {!videoURL ? (
                        <div className="min-h-[480px] border-2 border-dashed border-slate-800 rounded-[40px] flex flex-col items-center justify-center p-10 group hover:border-indigo-500 transition-all cursor-pointer relative">
                            <Upload className="text-slate-700 group-hover:text-indigo-500 mb-4 transition-colors" size={48} />
                            <p className="text-xs font-black uppercase tracking-widest text-slate-500 group-hover:text-slate-300 transition-colors">Upload Target Video Max 2 minutes</p>
                            <p className="text-[10px] text-slate-700 mt-1 uppercase tracking-wider">MP4 · MOV · WebM</p>
                            <input type="file" accept="video/*" className="absolute inset-0 opacity-0 cursor-pointer" onChange={handleVideoUpload} />
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <div className="relative rounded-[32px] overflow-hidden border-2 border-slate-800 bg-black" style={{ aspectRatio: '9/16' }}>
                                <video
                                    ref={videoRef} src={videoURL}
                                    className="absolute inset-0 w-full h-full object-cover opacity-0 pointer-events-none"
                                    loop muted autoPlay playsInline
                                />
                                <canvas
                                    ref={previewRef} width={540} height={960}
                                    className="w-full h-full"
                                    style={{ cursor: 'grab', touchAction: 'none' }}
                                    onMouseDown={onDragStart} onMouseMove={onDragMove}
                                    onMouseUp={onDragEnd}     onMouseLeave={onDragEnd}
                                    onTouchStart={onDragStart} onTouchMove={onDragMove} onTouchEnd={onDragEnd}
                                />
                                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 border border-white/10 rounded-full px-4 py-2 flex items-center gap-2 pointer-events-none">
                                    <Move size={12} className="text-indigo-400" />
                                    <span className="text-[10px] text-white font-bold uppercase tracking-wider">Drag to reposition</span>
                                </div>
                            </div>
                            <button
                                onClick={() => { setVideoFile(null); setVideoURL(null); }}
                                className="w-full text-slate-600 hover:text-slate-300 text-[10px] font-black uppercase tracking-widest transition-colors py-2"
                            >
                                Change Video
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <canvas ref={canvasRef} width={1080} height={1920} className="hidden" />
        </div>
    );
};

export default EmbedComment;
